/**
 * ACR Clinical Decision Support API.
 *
 * Backend contract — see acr_cds_integration_guide_markdown.md (v2):
 *
 *   POST /api/Acr/UploadReferralFile        → { Payload: { FileId } }
 *   POST /api/Acr/AnalyzeReferral
 *     Body:
 *       - fileId                (string, optional)        single FileId
 *       - fileIds               (string[], optional)      multiple FileIds (OCR'd & concatenated)
 *       - referralText          (string, optional)        free-form text
 *       - categorizedInputs     (object, optional)        { referral, priors, labs, diagnoses }
 *       - model                 (string, optional)        medilis.ai.V1(faster)|V2(deeper)|V3(thinking)
 *       - language              (string, optional)        output language for Explanation
 *     At least one of fileId / fileIds / referralText / categorizedInputs is required.
 *     Returns: { ExtractedText, DetectedAcrTopic, AcrTopicFound,
 *                MatchingRules[], Explanation, SelectedRuleId, SelectedRule }
 *
 *   POST /api/Acr/AskFollowup
 *     Body:
 *       - question              (string, required)
 *       - selectedRule          (AcrRuleInfo, required)   from AnalyzeReferral.Payload.SelectedRule
 *       - explanation           (string, required)        from AnalyzeReferral.Payload.Explanation
 *       - detectedAcrTopic      (string, optional)        from AnalyzeReferral.Payload.DetectedAcrTopic
 *       - conversationHistory   ({role,content}[], required) — full prior turns; backend is stateless
 *       - language              (string, optional)
 *       - model                 (string, optional)
 *     Returns: { Answer }
 *
 * Auth: Bearer token in Authorization header (attached by reportingClient).
 * No memberId/token fields are needed in the body.
 */

import { reportingClient } from "./httpClients/reportingClient";

const ACR_UPLOAD_PATH = "/api/Acr/UploadReferralFile";
const ACR_ANALYZE_PATH = "/api/Acr/AnalyzeReferral";
const ACR_FOLLOWUP_PATH = "/api/Acr/AskFollowup";

/**
 * Unwrap server envelope { Success/success, Payload/payload, ErrorInfo/errorInfo }.
 * Throws an Error with .code populated when the envelope reports failure.
 */
function parseEnvelope(response) {
  const data = response?.data ?? response ?? {};
  const ok = data.success ?? data.Success;
  if (!ok) {
    const info = data.errorInfo ?? data.ErrorInfo ?? {};
    const msg = info.message ?? info.Message ?? "Request failed";
    const err = new Error(msg);
    err.code = info.code ?? info.Code;
    throw err;
  }
  return data.payload ?? data.Payload ?? null;
}

/**
 * Upload a single referral file. The endpoint accepts one file per call and
 * returns one FileId — to send N files to AnalyzeReferral, call this N times
 * (e.g. via {@link uploadAcrReferralFilesParallel}) and pass the collected
 * IDs as `fileIds` to {@link analyzeAcrReferral}.
 *
 * Form field name is `"Files"` (plural) — confirmed by server validation
 * (the v2 spec example showed singular `"File"` but the controller binds
 * the same `Files` collection used by `/api/Protocol/UploadReferralFile`).
 *
 * @param {File} file
 * @returns {Promise<{fileId: string, originalFileName?: string}>}
 */
export async function uploadAcrReferralFile(file) {
  if (!file) throw new Error("A file is required");

  const formData = new FormData();
  formData.append("Files", file);
  // Do not set Content-Type manually for FormData; the browser sets boundary.
  const response = await reportingClient.post(ACR_UPLOAD_PATH, formData);
  const payload = parseEnvelope(response);
  return {
    fileId: payload?.fileId ?? payload?.FileId,
    originalFileName:
      payload?.originalFileName ?? payload?.OriginalFileName ?? file.name,
  };
}

/**
 * Upload several referral files in parallel and return their FileIds in the
 * same order as the input array. Fail-fast: if any single upload rejects
 * the whole call rejects — the caller should surface a toast and let the
 * user retry.
 *
 * @param {File[]} files
 * @returns {Promise<string[]>}
 */
export async function uploadAcrReferralFilesParallel(files) {
  const list = Array.isArray(files) ? files : [files].filter(Boolean);
  if (!list.length) return [];
  const results = await Promise.all(list.map((f) => uploadAcrReferralFile(f)));
  return results.map((r) => r.fileId).filter(Boolean);
}

/**
 * Run the full ACR analysis pipeline.
 *
 * Any combination of fileId/fileIds, referralText, and categorizedInputs is
 * allowed; the backend OCRs the files, merges with the structured inputs and
 * the free text, then runs topic detection + rule selection.
 *
 * @param {{
 *   fileId?: string,
 *   fileIds?: string[],
 *   referralText?: string,
 *   categorizedInputs?: { referral?: string, priors?: string, labs?: string, diagnoses?: string },
 *   model?: string,
 *   language?: string,
 * }} body
 * @returns {Promise<object>} ACR payload (see file header for shape)
 */
export async function analyzeAcrReferral(body) {
  const payload = {};

  if (body.fileId) payload.fileId = body.fileId;
  if (Array.isArray(body.fileIds) && body.fileIds.length) {
    payload.fileIds = body.fileIds;
  }

  const text = (body.referralText || "").trim();
  if (text) payload.referralText = text;

  if (body.categorizedInputs) {
    const ci = {};
    for (const key of ["referral", "priors", "labs", "diagnoses"]) {
      const v = (body.categorizedInputs[key] || "").trim();
      if (v) ci[key] = v;
    }
    if (Object.keys(ci).length) payload.categorizedInputs = ci;
  }

  if (
    !payload.fileId &&
    !payload.fileIds &&
    !payload.referralText &&
    !payload.categorizedInputs
  ) {
    throw new Error(
      "Provide at least one of: fileId, fileIds, referralText, categorizedInputs"
    );
  }

  if (body.model) payload.model = body.model;
  if (body.language) payload.language = body.language;

  const response = await reportingClient.post(ACR_ANALYZE_PATH, payload);
  return parseEnvelope(response);
}

/**
 * Ask a follow-up question about the selected ACR rule.
 *
 * Backend is stateless — the caller MUST pass the full prior conversation
 * (both user and assistant turns) plus the selected rule and explanation
 * on every call.
 *
 * @param {{
 *   question: string,
 *   selectedRule: object,
 *   explanation: string,
 *   detectedAcrTopic?: string,
 *   conversationHistory?: Array<{role: 'user'|'assistant', content: string}>,
 *   language?: string,
 *   model?: string,
 * }} body
 * @returns {Promise<{answer: string}>}
 */
export async function acrAskFollowup(body) {
  const payload = {
    question: body.question,
    selectedRule: body.selectedRule,
    explanation: body.explanation,
    conversationHistory: body.conversationHistory || [],
  };
  if (body.detectedAcrTopic) payload.detectedAcrTopic = body.detectedAcrTopic;
  if (body.language) payload.language = body.language;
  if (body.model) payload.model = body.model;

  const response = await reportingClient.post(ACR_FOLLOWUP_PATH, payload);
  const data = parseEnvelope(response);
  return { answer: data?.answer ?? data?.Answer ?? "" };
}
